import React from "react"
import Image from "next/image"
import { Link } from "components/Link"
import dateFormat from "utils/dateFormat"
import { trackSegmentPageElementClicked } from "data/tracking/segment/track/trackSegmentPageElementClicked"
import { preventWidow } from "utils/preventWidow"
import { blogSlug } from "components/Blog/utils/Blog.utils"
import { isStorybook } from "utils/storybookUtils"
import type { ReducedBlogV2ArticleType } from "types/blog-v2-types"

import { SixteenNine } from "packages/ui-shared/components/SixteenNine"
import {
  containerVariants,
  contentContainerVariants,
  tagVariants,
  headingClass,
  postDate,
} from "./BlogArticleCard.css"

type DefaultCardProps = {
  desktopCols?: 1 | 2 | 3
  width?: string
  headingSize?: "h2" | "h3" | "h4" | "h5"
  tagFontVariant?: "Default" | "AcuminMedium"
  minHeightForce?: boolean
}

export type BlogArticleCardV2Props = {
  article: ReducedBlogV2ArticleType
} & DefaultCardProps

const createLinkProps = (title: string, url: string) => {
  return {
    href: url,
    onClick: () =>
      trackSegmentPageElementClicked(
        "Homepage Blog articles test",
        6,
        title,
        url
      ),
    title,
  }
}

const HeaderSize = ({ headingSize, heading, styleClass }) => {
  const TagSize = headingSize
  return (
    <TagSize
      dangerouslySetInnerHTML={preventWidow(heading)}
      className={styleClass}
    />
  )
}

const getContainerVariant = (desktopCols: number) => {
  switch (desktopCols) {
    case 1:
      return containerVariants.OneCol
    case 2:
      return containerVariants.TwoCols
    default:
      return containerVariants.ThreeCols
  }
}

export const BlogArticleCardV2: React.FC<BlogArticleCardV2Props> = ({
  article,
  desktopCols = 2,
  headingSize = "h5",
  minHeightForce,
  tagFontVariant,
}) => {
  const { title, slug, tags, heroImage, publishDate } = article

  const blogLinkProps = createLinkProps(title, `${blogSlug}/${slug}`)

  const tagLinkProps = createLinkProps(
    tags[0].title,
    `${blogSlug}/tag/${tags[0].slug}`
  )

  return (
    <div className={getContainerVariant(desktopCols)}>
      <Link {...blogLinkProps}>
        <SixteenNine>
          {heroImage.src && (
            <Image
              src={heroImage.src}
              alt={heroImage.description || title}
              unoptimized={isStorybook}
              sizes="100vw"
              width={1600}
              height={900}
            />
          )}
        </SixteenNine>
      </Link>
      <div
        className={
          minHeightForce
            ? contentContainerVariants.forceMinHeight
            : contentContainerVariants.Default
        }
      >
        <Link {...tagLinkProps}>
          <p
            className={
              tagFontVariant ? tagVariants.AcuminMedium : tagVariants.Default
            }
            data-testid="post-title"
          >
            {tags[0].title}
          </p>
        </Link>
        <Link {...blogLinkProps}>
          <HeaderSize
            headingSize={headingSize}
            heading={title}
            styleClass={headingClass}
          />
        </Link>
        <p className={postDate}>{dateFormat(publishDate)}</p>
      </div>
    </div>
  )
}
